import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal/Modal';
import { Button } from '../ui/Button/Button';
import { Input } from '../ui/Input/Input';
import { Folder, File, CloudUpload, Search, Check, X, Image, FileText, FileSpreadsheet, FileType } from 'lucide-react';
import './LocalFolderBrowser.css';

interface LocalFile {
  id: string;
  name: string;
  type: string;
  size: number;
  lastModified: number;
  file: File;
  path: string;
  webViewLink?: string;
}

interface LocalFolderBrowserProps {
  visible: boolean;
  onCancel: () => void;
  onSelect: (files: LocalFile[]) => void;
  multiple?: boolean;
}

const LocalFolderBrowser: React.FC<LocalFolderBrowserProps> = ({
  visible,
  onCancel,
  onSelect,
  multiple = true,
}) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<LocalFile[]>([]);
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(t('localFolderBrowser.loading'));
  const dropZoneRef = useRef<HTMLDivElement>(null);

  // ファイルプレビューURL作成
  const createPreviewUrl = (file: File): string | null => {
    // 画像、PDF、テキストファイルのみプレビュー可能
    if (file.type.startsWith('image/') || 
        file.type === 'application/pdf' ||
        file.type.startsWith('text/')) {
      return URL.createObjectURL(file);
    }
    return null;
  };

  const processFileEntry = async (entry: any, path = ''): Promise<LocalFile[]> => {
    const results: LocalFile[] = [];
    
    if (entry.isFile) {
      const file = await new Promise<File>((resolve, reject) => {
        entry.file(resolve, reject);
      });
      
      const previewUrl = createPreviewUrl(file);
      
      results.push({
        id: `${path}/${file.name}_${file.lastModified}`,
        name: file.name,
        type: file.type || 'application/octet-stream',
        size: file.size,
        lastModified: file.lastModified,
        file,
        path: path || '/',
        webViewLink: previewUrl || undefined,
      });

      setLoadingMessage(t('localFolderBrowser.loadingWithFile', { filename: file.name, current: 1, total: 1 }));
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      const entries = await new Promise<any[]>((resolve, reject) => {
        const allEntries: any[] = [];
        const readEntries = () => {
          reader.readEntries((entries: any[]) => {
            if (entries.length === 0) {
              resolve(allEntries);
            } else {
              allEntries.push(...entries);
              readEntries();
            }
          }, reject);
        };
        readEntries();
      });
      
      for (const childEntry of entries) {
        const childResults = await processFileEntry(childEntry, `${path}/${entry.name}`);
        results.push(...childResults);
      }
    }
    
    return results;
  };

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setLoading(true);
    setLoadingMessage(t('localFolderBrowser.loading'));

    try {
      const items = Array.from(e.dataTransfer.items);
      const allFiles: LocalFile[] = [];

      for (const item of items) {
        const entry = item.webkitGetAsEntry();
        if (entry) {
          const files = await processFileEntry(entry);
          allFiles.push(...files);
        }
      }

      setFiles(allFiles);
      setLoadingMessage('');
    } catch (error) {
      console.error(t('error.localFileProcessError'), error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target === dropZoneRef.current) {
      setIsDragging(false);
    }
  }, []);

  const handleFileInputChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList) return;

    setLoading(true);
    setLoadingMessage(t('localFolderBrowser.loading'));
    const allFiles: LocalFile[] = [];

    try {
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        setLoadingMessage(t('localFolderBrowser.loadingWithFile', { filename: file.name, current: i + 1, total: fileList.length }));
        
        const previewUrl = createPreviewUrl(file);
        
        allFiles.push({
          id: `${file.webkitRelativePath || file.name}_${file.lastModified}`,
          name: file.name,
          type: file.type || 'application/octet-stream',
          size: file.size,
          lastModified: file.lastModified,
          file,
          path: file.webkitRelativePath ? 
            '/' + file.webkitRelativePath.split('/').slice(0, -1).join('/') : 
            '/',
          webViewLink: previewUrl || undefined,
        });
      }

      setFiles(allFiles);
      setLoadingMessage('');
    } catch (error) {
      console.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  }, []);

  const filteredFiles = files.filter(file => 
    file.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    file.path.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectFile = (fileId: string, checked: boolean) => {
    if (multiple) {
      setSelectedFileIds(prev =>
        checked ? [...prev, fileId] : prev.filter(id => id !== fileId)
      );
    } else {
      setSelectedFileIds(checked ? [fileId] : []);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedFileIds(checked ? filteredFiles.map(f => f.id) : []);
  };

  const handleOk = () => {
    const selectedFiles = files.filter(f => selectedFileIds.includes(f.id));
    onSelect(selectedFiles);
    // Clean up preview URLs
    files.forEach(file => {
      if (file.webViewLink && file.webViewLink.startsWith('blob:')) {
        URL.revokeObjectURL(file.webViewLink);
      }
    });
    setFiles([]);
    setSelectedFileIds([]);
    onCancel();
  };

  const handleCancel = () => {
    // Clean up preview URLs
    files.forEach(file => {
      if (file.webViewLink && file.webViewLink.startsWith('blob:')) {
        URL.revokeObjectURL(file.webViewLink);
      }
    });
    setFiles([]);
    setSelectedFileIds([]);
    onCancel();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return <Image size={20} />;
    if (type.includes('pdf')) return <FileText size={20} />;
    if (type.includes('spreadsheet') || type.includes('excel') || type.includes('csv')) return <FileSpreadsheet size={20} />;
    if (type.includes('document') || type.includes('word')) return <FileText size={20} />;
    if (type.startsWith('text/')) return <FileType size={20} />;
    return <File size={20} />;
  };

  const handlePreview = (file: LocalFile) => {
    if (file.webViewLink) {
      window.open(file.webViewLink, '_blank');
    }
  };

  return (
    <Modal
      isOpen={visible}
      onClose={handleCancel}
      title={t('localFolderBrowser.title')}
      size="xl"
      className="local-folder-browser-modal"
    >
      <div className="local-folder-browser">
        <div className="browser-header">
          <Input
            leftIcon={<Search size={16} />}
            placeholder={t('localFolderBrowser.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <input
            type="file"
            id="folder-input"
            webkitdirectory=""
            multiple
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />
          <Button
            variant="secondary"
            onClick={() => document.getElementById('folder-input')?.click()}
          >
            <Folder size={16} />
            {t('localFolderBrowser.selectFolder')}
          </Button>
        </div>

        <div
          ref={dropZoneRef}
          className={`drop-zone ${isDragging ? 'dragging' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {loading ? (
            <div className="loading-container">
              <div className="spinner" />
              <p className="loading-message">{loadingMessage}</p>
            </div>
          ) : files.length === 0 ? (
            <div className="empty-state">
              <CloudUpload size={48} className="empty-icon" />
              <p className="empty-message">
                {t('localFolderBrowser.dropMessage')}
                <br />
                {t('localFolderBrowser.dropMessageOr')}
              </p>
            </div>
          ) : (
            <>
              <div className="file-list-header">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedFileIds.length === filteredFiles.length && filteredFiles.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                  <span>{t('localFolderBrowser.selectAll', { selected: selectedFileIds.length, total: filteredFiles.length })}</span>
                </label>
              </div>
              
              <div className="file-list">
                {filteredFiles.map(file => (
                  <div key={file.id} className="file-item">
                    <input
                      type="checkbox"
                      className="file-checkbox"
                      checked={selectedFileIds.includes(file.id)}
                      onChange={(e) => handleSelectFile(file.id, e.target.checked)}
                    />
                    <div className="file-info">
                      <div className="file-icon">{getFileIcon(file.type)}</div>
                      <div className="file-details">
                        <div className="file-name-row">
                          <span className="file-name">{file.name}</span>
                          {file.webViewLink && (
                            <button
                              className="preview-button"
                              onClick={() => handlePreview(file)}
                              title="プレビュー"
                            >
                              {t('localFolderBrowser.preview')}
                            </button>
                          )}
                        </div>
                        <div className="file-meta">
                          <span className="file-path">{file.path}</span>
                          <span className="file-size">{formatFileSize(file.size)}</span>
                          <span className="file-modified">
                            {new Date(file.lastModified).toLocaleDateString('ja-JP')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {files.length > 0 && (
          <div className="file-summary">
            <span>{t('localFolderBrowser.filesTotal', { count: files.length })}</span>
            <span>{t('localFolderBrowser.filesSelected', { count: selectedFileIds.length })}</span>
          </div>
        )}

        <div className="modal-footer">
          <Button variant="secondary" onClick={handleCancel}>
            {t('common.cancel')}
          </Button>
          <Button 
            variant="primary" 
            onClick={handleOk}
            disabled={selectedFileIds.length === 0}
          >
            {t('localFolderBrowser.select', { count: selectedFileIds.length })}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default LocalFolderBrowser;